import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button, Alert, InputNumberWithButtons } from "@jugl-web/ui-kit";
import {
  useRestApiProvider,
  TopupConfigResponse,
  PendingInvoice,
} from "@jugl-web/rest-api";
import { useTranslations } from "@jugl-web/utils";
import useEntity from "@web-src/modules/entities/hooks/useEntity";
import { useSubscriptionV2 } from "../../../../providers/SubscriptionV2Provider";
import { CreditCardBase } from "../CreditCardBase/CreditCardBase";
import { PendingInvoiceAlert } from "../PendingInvoiceAlert";
import { ReactComponent as ArrowRightIcon } from "../../assets/arrow-right.svg";
import { ReactComponent as RechargeIcon } from "./assets/recharge.svg";
import { TOPUP_OPTIONS } from "../GetExtraCreditsCard/consts";
import { AutoRechargeDialogContent } from "./AutoRechargeDialogContent";

interface AutoRechargeCardProps {
  topupConfig?: TopupConfigResponse;
  onConfigUpdated?: () => void;
  pendingInvoice?: PendingInvoice;
  onRequestSwitch?: () => void;
}

export type AutoRechargeCardHandle = { open: () => void };

const creditsToAmount = (credits: number) =>
  TOPUP_OPTIONS.find((o) => o.credits === credits)?.amountInCents ?? 0;

const amountToCredits = (amount: number) =>
  TOPUP_OPTIONS.find((o) => o.amountInCents === amount)?.credits ?? 0;

export const AutoRechargeCard = forwardRef<
  AutoRechargeCardHandle,
  AutoRechargeCardProps
>(({ topupConfig, onConfigUpdated, pendingInvoice, onRequestSwitch }, ref) => {
  const { t } = useTranslations();
  const { entity } = useEntity();
  const { subscriptionV2Api } = useRestApiProvider();
  const [updateTopupConfig] = subscriptionV2Api.useUpdateTopupConfigMutation();
  const { hasPaymentMethod, updateCardDetails, planDefaultCredits } =
    useSubscriptionV2();

  const isConfigured = topupConfig && topupConfig.auto_recharge_amount > 0;
  const currentCredits = isConfigured
    ? amountToCredits(topupConfig.auto_recharge_amount)
    : 0;

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const dialogStateRef = useRef({
    threshold: 100,
    credits: TOPUP_OPTIONS[0].credits,
  });

  const handleOpen = useCallback(async () => {
    if (!hasPaymentMethod) {
      const cardResult = await updateCardDetails();
      if (cardResult.result !== "success") return;
    }
    dialogStateRef.current = {
      threshold:
        topupConfig?.auto_recharge_threshold ||
        Math.round(planDefaultCredits / 2),
      credits: isConfigured
        ? amountToCredits(topupConfig.auto_recharge_amount)
        : TOPUP_OPTIONS[0].credits,
    };
    setIsOpen(true);
  }, [
    hasPaymentMethod,
    topupConfig?.auto_recharge_threshold,
    topupConfig?.auto_recharge_amount,
    planDefaultCredits,
    isConfigured,
    updateCardDetails,
  ]);

  useImperativeHandle(ref, () => ({ open: handleOpen }), [handleOpen]);

  const handleDialogChange = useCallback(
    (threshold: number, credits: number) => {
      dialogStateRef.current = { threshold, credits };
      setIsValid(credits === 0 || threshold >= 0);
    },
    []
  );

  const handleClose = useCallback(() => {
    if (!isLoading) setIsOpen(false);
  }, [isLoading]);

  const handleSave = useCallback(async () => {
    const { credits, threshold } = dialogStateRef.current;
    const isDisabling = credits === 0;
    const amountInCents = isDisabling ? 0 : creditsToAmount(credits);
    if (!isDisabling && !amountInCents) return;
    if (!entity || threshold < 0) return;

    setIsLoading(true);
    try {
      await updateTopupConfig({
        entityId: entity.id,
        data: {
          auto_recharge_amount: amountInCents || null,
          auto_recharge_threshold: !amountInCents ? null : threshold || null,
        },
        meta: {
          successMessage: t({
            id: "cx-plans-page.auto-recharge.success",
            defaultMessage: "Auto recharge settings saved successfully!",
          }),
        },
      }).unwrap();

      setIsOpen(false);
      onConfigUpdated?.();
    } catch {
      // Error toast is handled globally by the API result pipeline
    } finally {
      setIsLoading(false);
    }
  }, [entity, updateTopupConfig, onConfigUpdated, t]);

  return (
    <>
      <CreditCardBase
        icon={<RechargeIcon className="shrink-0" />}
        title={t({
          id: "cx-plans-page.auto-recharge-card.title",
          defaultMessage: "Credits auto recharge",
        })}
        description={t({
          id: "cx-plans-page.auto-recharge-card.description",
          defaultMessage:
            "When your credits drop below set limit, we'll automatically top up your account with additional credits to keep your service running smoothly",
        })}
        price="$40"
        priceLabel={t({
          id: "cx-plans-page.auto-recharge-card.price-label",
          defaultMessage: "per 1,000 message credits",
        })}
        rightContent={
          isConfigured ? (
            <div className="flex shrink-0 items-center gap-2 font-secondary text-sm whitespace-nowrap text-black">
              {t({
                id: "cx-plans-page.auto-recharge-card.renew-with",
                defaultMessage: "Renew with",
              })}
              <div className="relative">
                <div
                  onClick={handleOpen}
                  className="absolute inset-0 z-[1] cursor-pointer"
                />
                <InputNumberWithButtons
                  value={currentCredits}
                  onChange={() => {}}
                  className="z-0 h-8"
                />
              </div>
              {t({
                id: "cx-plans-page.auto-recharge-card.extra-credits",
                defaultMessage: "extra credits",
              })}
            </div>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              iconEnd={<ArrowRightIcon />}
              onClick={handleOpen}
            >
              {t({
                id: "cx-plans-page.auto-recharge-card.button",
                defaultMessage: "Set Recharge",
              })}
            </Button>
          )
        }
        footerAlert={
          pendingInvoice && (
            <PendingInvoiceAlert
              text={t({
                id: "cx-plans-page.auto-recharge-card.pending-invoice-alert",
                defaultMessage:
                  "Payment require additional action ⚠️ Check to restore auto recharge",
              })}
              onCompletePayment={() =>
                window.open(pendingInvoice.invoice_url, "_blank")
              }
            />
          )
        }
      />
      <Alert
        isOpen={isOpen}
        onRequestClose={handleClose}
        title={t({
          id: "cx-plans-page.auto-recharge-dialog.title",
          defaultMessage: "\uD83D\uDD04 Credits auto recharge",
        })}
        textAlignment="left"
        content={
          <AutoRechargeDialogContent
            initialThreshold={dialogStateRef.current.threshold}
            initialCredits={dialogStateRef.current.credits}
            planDefaultCredits={planDefaultCredits}
            onChange={handleDialogChange}
            onRequestSwitch={
              onRequestSwitch
                ? () => {
                    setIsOpen(false);
                    onRequestSwitch();
                  }
                : undefined
            }
          />
        }
        buttons={[
          {
            text: t({
              id: "cx-plans-page.auto-recharge-dialog.cancel",
              defaultMessage: "Cancel",
            }),
            color: "grey",
            role: "close",
            isDisabled: isLoading,
          },
          {
            text: t({
              id: "cx-plans-page.auto-recharge-dialog.save",
              defaultMessage: "Save",
            }),
            color: "primary",
            isLoading,
            isDisabled: isLoading || !isValid,
            onClick: () => handleSave(),
          },
        ]}
      />
    </>
  );
});

AutoRechargeCard.displayName = "AutoRechargeCard";
